import {
	Badge,
	BottomSheet,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Input,
	Label,
	ResponsiveTable,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Skeleton,
	Switch,
} from "@ancatag/at-design";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, RefreshCw, Search, X } from "lucide-react";
import { useState } from "react";
import { CreateProjectDialog } from "../components/projects/CreateProjectDialog";
import { ProjectEmptyState } from "../components/projects/ProjectEmptyState";
import { useAuth } from "../context/AuthContext";
import { useProjectContext } from "../context/ProjectContext";
import { useLogs } from "../hooks/useLogs";
import type { UsageRecord } from "../services/logs";
import { listMyRouteConfigs } from "../services/route-configs";

export function RequestLogs() {
	const {
		selectedProject,
		isLoading: projectLoading,
		refreshProjects,
	} = useProjectContext();
	const { user } = useAuth();
	const isSuperAdmin = user?.role === "superAdmin";
	const [createProjectOpen, setCreateProjectOpen] = useState(false);
	const [page, setPage] = useState(1);
	const [limit] = useState(50);
	const [status, setStatus] = useState<string | undefined>(undefined);
	const [modelConfigId, setModelConfigId] = useState<string | undefined>(
		undefined,
	);
	const [cacheHit, setCacheHit] = useState<boolean | undefined>(undefined);
	const [search, setSearch] = useState("");
	const [autoRefresh, setAutoRefresh] = useState(false);
	const [selectedLog, setSelectedLog] = useState<UsageRecord | null>(null);

	// Fetch model configs for filter dropdown
	const { data: modelConfigs } = useQuery({
		queryKey: ["model-configs", selectedProject?.id],
		queryFn: () => listMyRouteConfigs({ page: 1, limit: 100 }),
		enabled: Boolean(selectedProject?.id),
	});

	const connectedModelConfigs =
		modelConfigs?.data?.filter((c) => {
			const primaryModel = c.models?.find((m) => m.isPrimary)?.modelConfig;
			return primaryModel?.connectionStatus === "connected";
		}) || [];

	// Fetch logs
	const { data: logsData, isLoading } = useLogs(
		{
			page,
			limit,
			status,
			modelConfigId,
			cacheHit,
			search: search || undefined,
		},
		{ autoRefresh, refreshInterval: 30000 },
	);

	const logs = logsData?.logs || [];
	const totalPages = logsData?.totalPages || 0;

	const getStatusBadge = (status: string) => {
		const variants: Record<string, string> = {
			completed: "bg-green-500/20 text-green-600 border-green-500/30",
			failed: "bg-red-500/20 text-red-600 border-red-500/30",
			cached: "bg-blue-500/20 text-blue-600 border-blue-500/30",
			sandbox: "bg-yellow-500/20 text-yellow-600 border-yellow-500/30",
		};
		return (
			variants[status] || "bg-gray-500/20 text-gray-600 border-gray-500/30"
		);
	};

	const getCacheBadge = (cacheHit: boolean, cacheLayer: string | null) => {
		if (!cacheHit) {
			return <Badge variant="outline">No Cache</Badge>;
		}
		if (cacheLayer === "hot") {
			return (
				<Badge
					variant="outline"
					className="bg-green-500/20 text-green-600 border-green-500/30"
				>
					Hot Cache
				</Badge>
			);
		}
		if (cacheLayer === "semantic") {
			return (
				<Badge
					variant="outline"
					className="bg-blue-500/20 text-blue-600 border-blue-500/30"
				>
					Semantic Cache
				</Badge>
			);
		}
		return <Badge variant="outline">Cached</Badge>;
	};

	if (projectLoading) {
		return <RequestLogsSkeleton />;
	}

	if (!selectedProject && !isSuperAdmin) {
		return (
			<div className="space-y-6">
				<h1 className="text-3xl font-bold text-foreground">Request Logs</h1>
				<p className="text-muted-foreground">
					View detailed request logs and usage metrics for your project.
				</p>
				<ProjectEmptyState
					onCreateProject={() => setCreateProjectOpen(true)}
					onRefresh={refreshProjects}
				/>
				<CreateProjectDialog
					open={createProjectOpen}
					onOpenChange={setCreateProjectOpen}
				/>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
				<div>
					<h1 className="text-2xl md:text-3xl font-bold text-foreground">
						Request Logs
					</h1>
					<p className="text-muted-foreground text-sm md:text-base">
						{isSuperAdmin
							? "Platform-wide request logs and activity"
							: `Detailed request logs for ${selectedProject?.name || "your project"}`}
					</p>
				</div>
				<div className="flex items-center gap-3">
					<div className="flex items-center gap-2">
						<Switch
							id="auto-refresh"
							checked={autoRefresh}
							onCheckedChange={setAutoRefresh}
						/>
						<Label htmlFor="auto-refresh" className="text-sm cursor-pointer">
							Auto-refresh
						</Label>
					</div>
					{autoRefresh && (
						<Badge variant="secondary" className="flex items-center gap-1">
							<RefreshCw className="h-3 w-3 animate-spin" />
							Updating...
						</Badge>
					)}
				</div>
			</div>

			{/* Filters */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Filters</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
						<div className="space-y-2">
							<Label className="text-xs text-muted-foreground">
								Search Request ID
							</Label>
							<div className="relative">
								<Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
								<Input
									placeholder="Search..."
									value={search}
									onChange={(e) => {
										setSearch(e.target.value);
										setPage(1);
									}}
									className="pl-8"
								/>
								{search && (
									<Button
										variant="ghost"
										size="icon"
										className="absolute right-1 top-1 h-7 w-7"
										onClick={() => {
											setSearch("");
											setPage(1);
										}}
									>
										<X className="h-4 w-4" />
									</Button>
								)}
							</div>
						</div>
						<div className="space-y-2">
							<Label className="text-xs text-muted-foreground">Status</Label>
							<Select
								value={status || "all"}
								onValueChange={(value) => {
									setStatus(value === "all" ? undefined : value);
									setPage(1);
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder="All Statuses" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">All Statuses</SelectItem>
									<SelectItem value="completed">Completed</SelectItem>
									<SelectItem value="failed">Failed</SelectItem>
									<SelectItem value="cached">Cached</SelectItem>
									<SelectItem value="sandbox">Sandbox</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label className="text-xs text-muted-foreground">
								Model Config
							</Label>
							<Select
								value={modelConfigId || "all"}
								onValueChange={(value) => {
									setModelConfigId(value === "all" ? undefined : value);
									setPage(1);
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder="All Models" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">All Models</SelectItem>
									{connectedModelConfigs.map((config) => (
										<SelectItem key={config.id} value={config.id}>
											{config.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label className="text-xs text-muted-foreground">Cache</Label>
							<Select
								value={
									cacheHit === undefined ? "all" : cacheHit ? "hit" : "miss"
								}
								onValueChange={(value) => {
									if (value === "all") {
										setCacheHit(undefined);
									} else {
										setCacheHit(value === "hit");
									}
									setPage(1);
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder="All" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">All</SelectItem>
									<SelectItem value="hit">Cache Hits</SelectItem>
									<SelectItem value="miss">Cache Misses</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Logs Table */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">
						Request Logs{" "}
						{logsData && `(${logsData.total.toLocaleString()} total)`}
					</CardTitle>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<RequestLogsTableSkeleton />
					) : logs.length === 0 ? (
						<div className="text-center py-12 text-muted-foreground">
							No logs found matching your filters.
						</div>
					) : (
						<>
							<ResponsiveTable
								data={logs}
								keyExtractor={(log) => log.id}
								columns={[
									{
										key: "time",
										header: "Time",
										mobileLabel: "Time",
										render: (log) => (
											<span className="font-mono text-xs">
												{new Date(log.createdAt).toLocaleString()}
											</span>
										),
									},
									{
										key: "requestId",
										header: "Request ID",
										mobileLabel: "Request ID",
										render: (log) => (
											<span className="font-mono text-xs">
												{log.requestId.slice(0, 8)}...
											</span>
										),
									},
									{
										key: "model",
										header: "Model",
										mobileLabel: "Model",
										render: (log) => (
											<span className="text-sm">{log.modelConfigName}</span>
										),
									},
									{
										key: "status",
										header: "Status",
										mobileLabel: "Status",
										render: (log) => (
											<Badge
												variant="outline"
												className={getStatusBadge(log.status)}
											>
												{log.status}
											</Badge>
										),
									},
									{
										key: "cache",
										header: "Cache",
										mobileLabel: "Cache",
										render: (log) =>
											getCacheBadge(log.cacheHit, log.cacheLayer),
									},
									{
										key: "latency",
										header: "Latency",
										mobileLabel: "Latency",
										render: (log) => (
											<span className="text-sm">
												{log.responseTimeMs ? `${log.responseTimeMs}ms` : "—"}
											</span>
										),
									},
									{
										key: "tokens",
										header: "Tokens",
										mobileLabel: "Tokens",
										render: (log) => (
											<span className="text-sm">
												{log.tokensTotal.toLocaleString()}
												{log.tokensSaved > 0 && (
													<span className="text-green-600 ml-1">
														(-{log.tokensSaved.toLocaleString()})
													</span>
												)}
											</span>
										),
									},
								]}
								onRowClick={(log) => setSelectedLog(log)}
								emptyMessage="No logs found matching your filters."
							/>

							{/* Pagination */}
							{totalPages > 1 && (
								<div className="flex items-center justify-between mt-4">
									<div className="text-sm text-muted-foreground">
										Page {page} of {totalPages}
									</div>
									<div className="flex items-center gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => setPage((p) => Math.max(1, p - 1))}
											disabled={page === 1}
										>
											<ChevronLeft className="h-4 w-4" />
											Previous
										</Button>
										<Button
											variant="outline"
											size="sm"
											onClick={() =>
												setPage((p) => Math.min(totalPages, p + 1))
											}
											disabled={page === totalPages}
										>
											Next
											<ChevronRight className="h-4 w-4" />
										</Button>
									</div>
								</div>
							)}
						</>
					)}
				</CardContent>
			</Card>

			{/* Log Detail Bottom Sheet/Dialog */}
			<BottomSheet
				open={!!selectedLog}
				onOpenChange={(open) => !open && setSelectedLog(null)}
				title="Request Details"
				maxHeight="max-h-[85vh]"
			>
				{selectedLog && (
					<div className="space-y-4">
						<div className="text-xs text-muted-foreground font-mono mb-4">
							Request ID: {selectedLog.requestId}
						</div>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<div>
								<Label className="text-xs text-muted-foreground">Status</Label>
								<div className="mt-1">
									<Badge
										variant="outline"
										className={getStatusBadge(selectedLog.status)}
									>
										{selectedLog.status}
									</Badge>
								</div>
							</div>
							<div>
								<Label className="text-xs text-muted-foreground">Cache</Label>
								<div className="mt-1">
									{getCacheBadge(selectedLog.cacheHit, selectedLog.cacheLayer)}
								</div>
							</div>
							<div>
								<Label className="text-xs text-muted-foreground">
									Model Config
								</Label>
								<div className="mt-1 text-sm">
									{selectedLog.modelConfigName}
								</div>
							</div>
							<div>
								<Label className="text-xs text-muted-foreground">
									Response Time
								</Label>
								<div className="mt-1 text-sm">
									{selectedLog.responseTimeMs
										? `${selectedLog.responseTimeMs}ms`
										: "—"}
								</div>
							</div>
							<div>
								<Label className="text-xs text-muted-foreground">
									Tokens Input
								</Label>
								<div className="mt-1 text-sm">
									{selectedLog.tokensInput.toLocaleString()}
								</div>
							</div>
							<div>
								<Label className="text-xs text-muted-foreground">
									Tokens Output
								</Label>
								<div className="mt-1 text-sm">
									{selectedLog.tokensOutput.toLocaleString()}
								</div>
							</div>
							<div>
								<Label className="text-xs text-muted-foreground">
									Tokens Total
								</Label>
								<div className="mt-1 text-sm">
									{selectedLog.tokensTotal.toLocaleString()}
								</div>
							</div>
							<div>
								<Label className="text-xs text-muted-foreground">
									Tokens Saved
								</Label>
								<div className="mt-1 text-sm text-green-600">
									{selectedLog.tokensSaved.toLocaleString()}
								</div>
							</div>
							{selectedLog.errorMessage && (
								<div className="col-span-2">
									<Label className="text-xs text-muted-foreground">Error</Label>
									<div className="mt-1 text-sm text-red-600">
										{selectedLog.errorMessage}
									</div>
								</div>
							)}
							<div>
								<Label className="text-xs text-muted-foreground">
									Created At
								</Label>
								<div className="mt-1 text-sm font-mono">
									{new Date(selectedLog.createdAt).toLocaleString()}
								</div>
							</div>
						</div>
					</div>
				)}
			</BottomSheet>
		</div>
	);
}

function RequestLogsSkeleton() {
	return (
		<div className="space-y-4">
			<Skeleton className="h-8 w-64" />
			<Skeleton className="h-32 w-full" />
			<Skeleton className="h-96 w-full" />
		</div>
	);
}

function RequestLogsTableSkeleton() {
	return (
		<div className="space-y-2">
			{[1, 2, 3, 4, 5].map((i) => (
				<Skeleton key={i} className="h-12 w-full" />
			))}
		</div>
	);
}

export default RequestLogs;
